import Vue from "vue";
import {
  initialAccountState,
  initialBankDetailsState,
  initialCookieState,
  initialDiscountState,
  initialHouseInformationState,
  initialPersonalDetailsState,
  initialReferralState,
  initialUiState,
} from "@/store/initialState";

export default {
  SET_REFERRAL_STATE: (state, payload) => {
    state.referral = payload;
  },
  SET_AFFILIATE_STATE: (state, payload) => {
    state.affiliate = payload;
  },
  SET_DISCOUNT_STATE: (state, payload) => {
    state.discount = {
      ...state.discount,
      ...payload,
    };
  },
  SET_POSTCODE: (state, payload) => {
    state.houseInformation.postcode = payload.toUpperCase();
  },
  SET_LETTING_AGENT: (state, payload) => {
    state.houseInformation.lettingAgentName = payload;
  },
  INCREMENT_HOUSEMATE_COUNT: (state) => {
    state.housemateCount++;
  },
  DECREMENT_HOUSEMATE_COUNT: (state) => {
    state.housemateCount--;
  },
  SET_HOUSEMATE_COUNT: (state, payload) => {
    state.housemateCount = payload;
  },
  SHOW_BACK_BTN: (state) => {
    state.uiState.showBackBtn = true;
  },
  HIDE_BACK_BTN: (state) => {
    state.uiState.showBackBtn = false;
  },
  SHOW_LOADING_PANEL: (state) => {
    state.uiState.isLoading = true;
  },
  HIDE_LOADING_PANEL: (state) => {
    state.uiState.isLoading = false;
  },
  IS_VIA_SHARELINK: (state, payload) => {
    state.uiState.isViaShareLink = payload;
  },
  SET_UI_STATE_PROP: (state, payload) => {
    state.uiState = {
      ...state.uiState,
      ...payload,
    };
  },
  SET_LEAD_ID: (state, leadId) => {
    state.leadId = leadId;
  },
  SET_PRODUCTS: (state, payload) => {
    //inject extra properties to products here to avoid touching the backend
    state.products = payload.map((product) => {
      product.optional = state.optionalProductTypes.includes(product.type);

      for (let i = 0; i < product.options.length; i++) {
        product.options[i].optional = product.optional;
        product.options[i].enabled = true; //enabled by default
      }

      return product;
    });

    // Select all default products
    for (let i = 0; i < payload.length; i++) {
      const product = payload[i];
      const selectedOption = product.options.find(
        (op) => product.defaultOption === op.code,
      );

      Vue.set(state.pickedProductPriceCodes, product.type, selectedOption.code);
    }
  },
  SET_PACKAGE_PRICE: (state, payload) => {
    state.packagePrice = payload;
  },
  SET_PRODUCT_OPTION: (state, payload) => {
    Vue.set(state.pickedProductPriceCodes, payload.type, payload.code);
  },
  SET_UNPICKED_PRODUCT_OPTION: (state, payload) => {
    Vue.set(
      state.pickedProductPriceCodes,
      payload.picked.type,
      payload.picked.code,
    );
    Vue.set(
      state.unpickedProductPriceCodes,
      payload.unpicked.type,
      payload.unpicked.code,
    );
  },
  SET_AUTOCOMPLETED_ADDRESS: (state, payload) => {
    state.selectedAutocompletedAddress = payload;
  },
  SET_LEAD_TENANT_ADDRESS: (state, payload) => {
    state.houseInformation.address = payload;
  },
  SET_PERSONAL_DETAILS_PROP: (state, payload) => {
    state.personalDetails = {
      ...state.personalDetails,
      ...payload,
    };
  },
  SET_TENANCY_START_DATE: (state, payload) => {
    state.houseInformation.startDate = payload;
  },
  SET_TENANCY_END_DATE: (state, payload) => {
    state.houseInformation.endDate = payload;
  },
  SET_BROADBAND_URGENCY: (state, payload) => {
    state.houseInformation.broadbandUrgent = payload;
  },
  SET_BROADBAND_DATE: (state, payload) => {
    state.houseInformation.broadbandDate = payload;
  },
  SET_ADDRESS_INPUT_TYPE: (state, payload) => {
    state.uiState.isManualAddress = payload;
  },
  SET_ADDRESS_ID: (state, payload) => {
    state.addressId = payload;
  },
  SET_BANK_DETAILS_PROP: (state, payload) => {
    state.bankDetails = {
      ...state.bankDetails,
      ...payload,
    };
  },
  SET_ACCOUNT_INFORMATION: (state, payload) => {
    state.accountInformation.guid = payload.guid;
    state.accountInformation.accountCreator = payload.name;
    state.accountInformation.address = payload.address;
    state.accountInformation.postcode = payload.postcode;
    state.accountInformation.tenancyStartDate = payload.tenancy.startDate;
    state.accountInformation.tenancyEndDate = payload.tenancy.endDate;
    state.accountInformation.broadbandDate = payload.broadbandDate;
    state.accountInformation.products = payload.products;
    state.accountInformation.discountDescription = payload.discountDescription;
    state.accountInformation.usedDiscount = payload.usedDiscount;
    state.accountInformation.nonDiscountedPppwPence = payload.nonDiscountedPppw;
    state.accountInformation.pppwPence = payload.pppw;

    state.houseInformation.startDate = payload.tenancy.startDate;
    state.houseInformation.endDate = payload.tenancy.endDate;
    state.houseInformation.broadbandDate = payload.broadbandDate;
    state.houseInformation.broadbandUrgent = false; // This is used to decide if to show broadband date in the join flow.
    state.houseInformation.address = payload.fullAddress;
    state.products = payload.products;
    state.discount.isValid = payload.usedDiscount;
    state.housemateCount = payload.housemateCount;
  },
  SET_ACCOUNT_PROP: (state, payload) => {
    state.accountInformation = {
      ...state.accountInformation,
      ...payload,
    };
  },
  SET_VALIDATION_ERRORS(state, errors) {
    state.validationErrors = errors;
  },
  CLEAR_VALIDATION_ERRORS(state) {
    state.validationErrors = {};
  },
  RESET_STATE: (state) => {
    Vue.set(state, "leadId", null);
    Vue.set(state, "products", []);
    Vue.set(state, "uiState", initialUiState());
    Vue.set(state, "referral", initialReferralState());
    Vue.set(state, "houseInformation", initialHouseInformationState());
    Vue.set(state, "personalDetails", initialPersonalDetailsState());
    Vue.set(state, "bankDetails", initialBankDetailsState());
    Vue.set(state, "accountInformation", initialAccountState());
    Vue.set(state, "cookieState", initialCookieState());
    Vue.set(state, "discount", initialDiscountState());
  },
  CAPITALISE_NAME: (state) => {
    const titleCase = (s) => {
      if (!s) {
        return s;
      }

      if (s.length === 1) {
        return s.toUpperCase();
      }

      return `${s.charAt(0).toUpperCase()}${s.substr(1)}`;
    };

    if (state.personalDetails.firstname) {
      Vue.set(
        state.personalDetails,
        "firstname",
        titleCase(state.personalDetails.firstname),
      );
    }

    if (state.personalDetails.lastname) {
      Vue.set(
        state.personalDetails,
        "lastname",
        titleCase(state.personalDetails.lastname),
      );
    }
  },
  COOKIE_ENABLE_PERSONAL_EXPERIENCE: (state, payload) => {
    Vue.set(state.cookieState, "personal", payload);
  },
  SET_COOKIE_BANNER_EXPANDED: (state) => {
    Vue.set(state, "cookieBannerExpanded", !state.cookieBannerExpanded);
  },
  SET_FEATURE_FLAGS: (state, payload) => {
    state.featureFlags = payload.reduce((flags, flag) => {
      flags[flag.key] = flag;
      return flags;
    }, {});
    state.featureFlagsChecked = true;
  },
  SET_MINIMUM_CONTRACT_LENGTH: (state, payload) => {
    state.uiState.minTenancyLengthMonths = payload;
  },
};
